import React, { useRef } from 'react';

function SearchBar({
  value,
  onChange,
  onSubmit,
  placeholder = 'Search for a product…',
  suggestions = [],
  onSuggestionSelect,
  isLoading = false,
}) {
  const inputRef = useRef(null);

  function handleKeyDown(e) {
    if (e.key === 'Enter') {
      onSubmit(value);
    }
  }

  function handleClear() {
    onChange('');
    inputRef.current?.focus();
  }

  function handleSuggestionClick(suggestion) {
    if (onSuggestionSelect) onSuggestionSelect(suggestion);
  }

  return (
    <div className="relative">
      {/* Input row */}
      <div className="bg-surface-card border border-neutral-200 rounded-md shadow-sm px-4 py-3 flex items-center gap-3 focus-within:border-primary-500 focus-within:ring-2 focus-within:ring-primary-500 transition-colors duration-fast">
        {/* Search icon */}
        <svg
          className="w-5 h-5 text-neutral-400 flex-shrink-0"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={1.5}
          stroke="currentColor"
          aria-hidden="true"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" />
        </svg>

        {/* Input */}
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className="flex-1 font-sans text-body text-neutral-800 placeholder:text-neutral-400 bg-transparent outline-none"
        />

        {/* Spinner */}
        {isLoading && (
          <svg
            className="w-5 h-5 text-neutral-400 flex-shrink-0 animate-spin"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
          </svg>
        )}

        {/* Clear button */}
        {!isLoading && value.length > 0 && (
          <button
            type="button"
            onClick={handleClear}
            className="text-neutral-400 hover:text-neutral-600 flex-shrink-0 focus:outline-none"
            aria-label="Clear search"
          >
            <svg
              className="w-5 h-5"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              aria-hidden="true"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>

      {/* Suggestions dropdown */}
      {suggestions.length > 0 && (
        <ul className="absolute top-full left-0 right-0 mt-1 bg-surface-card border border-neutral-200 rounded-md shadow-md overflow-hidden z-10">
          {suggestions.map((suggestion, index) => (
            <li key={index}>
              <button
                type="button"
                onClick={() => handleSuggestionClick(suggestion)}
                className="w-full text-left px-4 py-3 font-sans text-body text-neutral-800 cursor-pointer transition-colors duration-fast hover:bg-primary-50 hover:text-primary-700 focus:outline-none focus:bg-primary-50 focus:text-primary-700"
              >
                {suggestion}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default SearchBar;
